import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Play, ShieldAlert, ScanSearch, Scale, Zap, FileCheck2, ArrowRight, RotateCcw } from "lucide-react";
import { SIMULATOR_SCENARIOS, DEMO_DISCLAIMER, severityClasses, SEVERITY, actionClasses } from "../../demo/demoData";
import { useDemo } from "../../demo/DemoContext";
import { Button } from "../ui/button";

const STEPS = [
  { key: "event", label: "Event", icon: ShieldAlert },
  { key: "detection", label: "Detection", icon: ScanSearch },
  { key: "policy", label: "Policy", icon: Scale },
  { key: "action", label: "Action", icon: Zap },
  { key: "outcome", label: "Outcome", icon: FileCheck2 },
  { key: "evidence", label: "Evidence", icon: FileCheck2 },
];

function reduced() {
  return window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export default function RiskSimulator({ onOpenHeimdall, onGenerate }) {
  const { runScenario } = useDemo();
  const [scenarioId, setScenarioId] = useState("A");
  const [revealed, setRevealed] = useState(0);
  const [running, setRunning] = useState(false);
  const [incidentId, setIncidentId] = useState(null);

  const scenario = SIMULATOR_SCENARIOS.find((s) => s.id === scenarioId);

  const run = () => {
    setRunning(true);
    setRevealed(0);
    const created = runScenario(scenarioId);
    setIncidentId(created ? created.id : null);
    if (reduced()) {
      setRevealed(STEPS.length);
      setRunning(false);
      return;
    }
    let n = 0;
    const iv = setInterval(() => {
      n += 1;
      setRevealed(n);
      if (n >= STEPS.length) {
        clearInterval(iv);
        setRunning(false);
      }
    }, 650);
  };

  const reset = () => { setRevealed(0); setIncidentId(null); };

  const stepContent = {
    event: `${scenario.interactionType} on ${scenario.channel}.`,
    detection: scenario.detection,
    policy: `${scenario.policy} (${scenario.policyVersion}).`,
    action: `${scenario.action}.`,
    outcome: scenario.outcome,
    evidence: `${scenario.nextStep} Reference ${incidentId || "pending"}.`,
  };

  const sev = SEVERITY[scenario.severity];

  return (
    <div className="rounded-lg border border-white/10 bg-navy-900/60 overflow-hidden">
      <div className="p-4 border-b border-white/10 flex flex-wrap items-center gap-2">
        {SIMULATOR_SCENARIOS.map((s) => (
          <button
            key={s.id}
            onClick={() => { setScenarioId(s.id); reset(); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors border ${
              scenarioId === s.id ? "bg-primary/12 border-primary/40 text-foreground" : "border-white/10 text-muted-foreground hover:text-foreground hover:bg-white/5"
            }`}
          >
            {s.title}
          </button>
        ))}
      </div>

      <div className="p-5 sm:p-7">
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Synthetic input · {scenario.channel}</p>
          <p className="mt-2 text-sm text-foreground font-mono leading-relaxed">“{scenario.input}”</p>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button onClick={run} disabled={running} className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Play className="mr-1.5 h-4 w-4" /> {running ? "Running\u2026" : "Test this interaction"}
          </Button>
          {revealed > 0 && !running && (
            <Button variant="outline" onClick={reset} className="border-white/15 bg-transparent hover:bg-white/5 text-foreground">
              <RotateCcw className="mr-1.5 h-4 w-4" /> Reset
            </Button>
          )}
          <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${severityClasses[sev.tone]}`}>
            Severity: {scenario.severity}
          </span>
        </div>

        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {STEPS.map((st, i) => {
            const Icon = st.icon;
            const on = i < revealed;
            return (
              <div key={st.key} className={`rounded-xl border p-4 transition-all duration-500 ${on ? "border-primary/30 bg-primary/[0.05] opacity-100" : "border-white/10 bg-white/[0.02] opacity-45"}`}>
                <div className="flex items-center gap-2">
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-primary/12 text-primary"><Icon className="h-4 w-4" /></span>
                  <span className="text-xs uppercase tracking-wide text-muted-foreground">{st.label}</span>
                </div>
                <p className="mt-2.5 text-sm text-foreground leading-relaxed min-h-[2.5rem]">{on ? stepContent[st.key] : "\u2014"}</p>
              </div>
            );
          })}
        </div>

        {revealed >= STEPS.length && (
          <div className="mt-5 flex flex-wrap items-center gap-3 rounded-xl border border-primary/25 bg-primary/[0.06] p-4">
            <span className={`inline-flex items-center rounded-md border px-2.5 py-1 text-xs font-semibold ${actionClasses[scenario.action]}`}>
              Action: {scenario.action}
            </span>
            <span className="text-sm text-muted-foreground">This interaction is now an incident in Heimdall.</span>
            <div className="flex gap-2 ml-auto">
              <Button size="sm" variant="outline" className="border-white/15 bg-transparent hover:bg-white/5 text-foreground" onClick={() => onOpenHeimdall && onOpenHeimdall(incidentId)}>
                Open in Heimdall <ArrowRight className="ml-1 h-3.5 w-3.5" />
              </Button>
              <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => onGenerate && onGenerate(incidentId)}>
                Generate evidence
              </Button>
            </div>
          </div>
        )}

        <p className="mt-5 text-xs text-muted-foreground/80 border-t border-white/10 pt-4">{DEMO_DISCLAIMER}</p>
      </div>
    </div>
  );
}
